import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isObject } from "lodash-es";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { storeGeneratedSurvey } from "../utils/generated-survey";

import type { UUID } from "../../../utils/uuid";
import type { FC } from "react";

/**
 * Listen's Duplicate Survey, from its dashboard: the latest revision under a new name, opened in
 * Listen's editor as a new survey, which is only saved from there.
 */
export const DuplicateSurveyButton: FC<{ id: UUID; title: string }> = ({ id, title }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const { handleOpen, handleClose, open } = useDialog();

  const latestRevision = useSupabaseCallback(
    async ({ supabase, account_id }) =>
      await supabase
        .from("survey_revision")
        .select("survey_content")
        .eq("account_id", account_id)
        .eq("survey_id", id)
        .order("revision", { ascending: false })
        .limit(1)
        .maybeSingle(),
    [id],
  );

  const duplicate = async (): Promise<void> => {
    setDuplicating(true);
    const response = await latestRevision();
    if (!response || response.error) {
      setDuplicating(false);
      console.error("Failed to duplicate survey:", response?.error);
      toast.error("Failed to duplicate survey");
      return;
    }

    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Supabase types survey_content as Json; it's a survey's JSON
    const content = (response.data?.survey_content ?? {}) as { title?: unknown };
    const key = storeGeneratedSurvey({
      ...content,
      title: isObject(content.title) ? { default: name } : name,
    });
    void navigate(`/listen/survey/new?key=${key}&title=${encodeURIComponent(name)}`);
  };

  return (
    <>
      <Tooltip title={`Duplicate Survey: ${title}`}>
        <IconButton data-analytics-id="survey-clone" onClick={handleOpen}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle variant="h6">Duplicate Survey</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={1}>
            <TextField
              id="title"
              label="Survey Name"
              type="text"
              fullWidth
              variant="standard"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Typography variant="body1">
              This will make a new survey with the same questions and settings, but no results will
              be copied over. After editing it, you’ll need to launch the new survey before sending
              it.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            data-analytics-id="survey-clone-cancel"
            disabled={duplicating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void duplicate()}
            data-analytics-id="survey-clone-confirm"
            loading={duplicating}
            disabled={!name.trim()}
          >
            Duplicate Survey
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
